import React, { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RefreshCw, Loader2, ShieldAlert } from 'lucide-react';

const PC_PACK = 'industry.insurance.property_casualty';
const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

function Rate({ testId, title, value, note, warn }) {
  return (
    <div className={`rounded-xl border p-4 ${warn ? 'border-amber-500/40 bg-amber-500/5' : 'border-border/40 bg-card/40'}`} data-testid={testId}>
      <div className="text-2xl font-bold text-foreground">{pct(value)}</div>
      <div className="text-sm font-medium text-foreground mt-0.5">{title}</div>
      <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{note}</p>
    </div>
  );
}

export default function RetentionPreviewPage() {
  const [grace, setGrace] = useState(30);
  const [rewrite, setRewrite] = useState('renewal');
  const [zeroDenom, setZeroDenom] = useState('null');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.retentionPreview(PC_PACK, {
        renewal_grace_period: Number(grace), rewrite_treatment: rewrite, zero_denominator_behavior: zeroDenom,
      });
      setRes(r);
    } catch (e) { toast.error(e.message || 'Could not run retention preview'); }
    finally { setBusy(false); }
  };

  const d = res?.diagnostics || {};
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="retention-preview-page">
      <div>
        <h1 className="text-xl font-heading font-semibold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-primary" /> P&amp;C Retention preview</h1>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          Policy and premium retention depend on a chosen methodology — how you define the expiring-term population and
          match each expiring term to a renewal. Pick a methodology below and preview the result on the governed retention
          fixture. Matching handles changed policy numbers, rewrites, a grace period and duplicate successors. These
          calculations stay <span className="text-foreground">draft until you certify them</span>; nothing here auto-certifies.
        </p>
      </div>

      <div className="rounded-xl border border-border/50 bg-card/40 p-4 flex flex-wrap items-end gap-4" data-testid="retention-controls">
        <div>
          <Label className="text-xs">Renewal grace period (days)</Label>
          <Input type="number" value={grace} onChange={(e) => setGrace(e.target.value)} className="h-9 w-40 mt-1" data-testid="retention-grace" />
        </div>
        <div>
          <Label className="text-xs">Rewrite treatment</Label>
          <select value={rewrite} onChange={(e) => setRewrite(e.target.value)} className="h-9 rounded-md border border-border/50 bg-background px-2 text-sm mt-1 block" data-testid="retention-rewrite">
            <option value="renewal">Rewrite counts as renewal</option>
            <option value="non_renewal">Rewrite is not a renewal</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">If nothing is eligible</Label>
          <select value={zeroDenom} onChange={(e) => setZeroDenom(e.target.value)} className="h-9 rounded-md border border-border/50 bg-background px-2 text-sm mt-1 block" data-testid="retention-zerodenom">
            <option value="null">Show no result</option>
            <option value="zero">Show 0%</option>
          </select>
        </div>
        <Button onClick={run} disabled={busy} className="h-9 gap-1" data-testid="retention-run">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Run preview
        </Button>
      </div>

      {res && (
        <div className="space-y-4" data-testid="retention-results">
          <div className="grid sm:grid-cols-3 gap-3">
            <Rate testId="retention-policy" title="Policy Retention Rate" value={res.policy_retention_rate}
              note={`${res.renewed_policy_count} renewed of ${res.eligible_expiring_policy_count} eligible expiring policies.`} />
            <Rate testId="retention-premium" title="Premium Retention Rate" value={res.premium_retention_rate}
              note={`${res.retained_expiring_premium?.toLocaleString()} retained of ${res.eligible_expiring_premium?.toLocaleString()} eligible expiring premium.`} />
            <Rate testId="retention-ratio" title="Renewal Premium Ratio" value={res.renewal_premium_ratio} warn={res.renewal_premium_ratio > 1}
              note="Renewal-term written premium ÷ eligible expiring premium. May exceed 100% when rates rise on renewal — this is NOT policy retention." />
          </div>
          <div className="rounded-xl border border-border/40 bg-card/30 p-4 text-[13px] text-muted-foreground" data-testid="retention-diagnostics">
            <div className="font-medium text-foreground mb-1">Matching diagnostics</div>
            <ul className="space-y-0.5">
              <li>Changed policy numbers matched by successor link: <span className="text-foreground">{d.changed_policy_number_matches}</span></li>
              <li>Duplicate successors de-duplicated: <span className="text-foreground">{d.duplicate_successors_deduped}</span></li>
              <li>Excluded — successor outside grace period: <span className="text-foreground">{d.excluded_grace_period}</span></li>
              <li>Excluded — rewrite not treated as renewal: <span className="text-foreground">{d.excluded_rewrite}</span></li>
            </ul>
          </div>
          <p className="text-[11px] text-amber-600 dark:text-amber-300" data-testid="retention-governance">{res.governance}</p>
        </div>
      )}
    </div>
  );
}

export { RetentionPreviewPage };
